/**
 * `CouponDto` / `CouponBindingModel`, from `Gopanza.Api.Services.Abstractions`.
 *
 * One table behind two tabs: `search/discountcode/stores` returns the coupons
 * with no `UserId`, `search/discountcode/users` the ones with one. Nothing else
 * distinguishes them.
 */

/**
 * `CouponType` — what the discount *is*.
 *
 * Neither legacy editor exposed it; both hard-coded `4` (`GiftCard`) on every
 * code they created, so the column carries one value in practice. Kept at the
 * same constant rather than guessing at a better one, because changing what
 * existing redemption logic sees is not a migration decision.
 */
export const COUPON_TYPE = {
  freeDelivery: 0,
  delivery: 1,
  reimbursement: 2,
  food: 3,
  giftCard: 4,
  promotional: 5,
  productSpecific: 6,
  brandSpecific: 7,
  percentDiscount: 8,
  sponsorship: 9,
} as const;

/**
 * `CouponApplicationType` — how often one code can be used. Both legacy
 * editors labelled this "Type", beside nothing called application type, which
 * is why it reads as the coupon's kind and is not.
 */
export const APPLICATION_TYPE = {
  oneTime: 0,
  reUsable: 1,
  oncePerUser: 2,
} as const;

/** `CouponApplicableService`. */
export const APPLIES_TO = {
  pickup: 0,
  delivery: 1,
  pickupAndDelivery: 2,
} as const;

export interface PromoCode {
  id: string;
  createdAt: string | null;
  code: string | null;
  type: number;
  userId: string | null;
  expiresAt: string | null;
  value: number;
  applicationType: number;
  minimumAmount: number;
  appliesTo: number;
  matchString: string | null;
  itemId: string | null;
  amountCap: number;
  hasAmountCap: boolean;
  businessId: string | null;
  /** Resolved through the navigation properties by the mapping profile. */
  customerName: string | null;
  businessName: string | null;

  /*
   * `isValid`, `isExpired`, `isRedeemed`, `isDifferentUser` and
   * `isDifferentStore` are on the DTO but deliberately absent here.
   *
   * The `Coupon` entity has no such columns and the mapping profile configures
   * only `CustomerName` and `BusinessName`, so AutoMapper leaves all five at
   * `false` on both search responses. They are set by hand elsewhere, in the
   * redemption check. Reading them here would render "not expired" beside a
   * code that expired last year.
   */
}

/** What `POST businesses/discountcode` accepts. Create only. */
export interface PromoCodeInput {
  code: string;
  type: number;
  value: number;
  minimumAmount: number;
  applicationType: number;
  appliesTo: number;
  expiresAt: string | null;
  businessId: string | null;
  /** Set for a customer code; null makes it a store code. */
  userId: string | null;
  email: string | null;
  friendCode: string | null;
  /** Inverted from the editor's "notify the customer" switch. */
  silentMode: boolean;
  message: string | null;
  matchString: string | null;
  itemId: number;
  amountCap: number;
  hasAmountCap: boolean;
}

export interface PromoCodeSearchResult {
  count: number;
  coupons: PromoCode[];
}

export interface Redemption {
  customerFirstName: string | null;
  customerLastName: string | null;
  customerEmail: string | null;
  customerPhoneNumber: string | null;
  confirmationCode: string | null;
  createdAt: string;
  businessName: string | null;
}

/** Expiry is the only lifecycle a code has that the list can see. */
export function isExpired(code: PromoCode, now = Date.now()): boolean {
  if (!code.expiresAt) return false;
  const at = Date.parse(`${code.expiresAt}Z`);
  return !Number.isNaN(at) && at < now;
}

export function emptyCode(kind: 'store' | 'customer'): PromoCodeInput {
  return {
    code: '',
    // Both legacy editors sent this constant; see COUPON_TYPE above.
    type: COUPON_TYPE.giftCard,
    value: 0,
    minimumAmount: 0,
    // A store code is public, so once per user is the sane default; a customer
    // code is already addressed to one person, so one use is.
    applicationType:
      kind === 'store' ? APPLICATION_TYPE.oncePerUser : APPLICATION_TYPE.oneTime,
    appliesTo: APPLIES_TO.pickupAndDelivery,
    expiresAt: null,
    businessId: null,
    userId: null,
    email: null,
    friendCode: null,
    silentMode: false,
    message: null,
    matchString: '',
    itemId: 0,
    amountCap: 0,
    hasAmountCap: false,
  };
}
